import { useCallback, useState } from "react";
import { api, ApiClientError } from "../../lib/apiClient.js";
import type { CustomerDto } from "@grizzly/shared";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";

interface Form {
  legalName: string;
  displayName: string;
  phone: string;
  email: string;
  notes: string;
}

const EMPTY_FORM: Form = {
  legalName: "",
  displayName: "",
  phone: "",
  email: "",
  notes: "",
};

export function CustomerCreateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(customer: CustomerDto): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setFieldErrors({});
    try {
      const res = await api.createCustomer(form);
      push(
        t("customers.createdToast"),
        t("customers.createdToastDesc", { name: res.customer.legalName })
      );
      setForm(EMPTY_FORM);
      onOpenChange(false);
      onCreated(res.customer);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "validation_failed") {
        setFieldErrors(err.fieldIssues());
      } else if (err instanceof ApiClientError) {
        push(
          err.message,
          err.requestId ? `${t("common.requestId", { id: err.requestId })}` : undefined,
          "danger"
        );
      }
    } finally {
      setBusy(false);
    }
  }, [form, onCreated, onOpenChange, push, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("customers.create.title")}
      description={t("customers.create.description")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={busy}>
            {busy ? t("common.creating") : t("customers.create.button")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextInput
          label={t("customers.field.legalName")}
          required
          value={form.legalName}
          error={fieldErrors["legalName"] ?? null}
          onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
        />
        <TextInput
          label={t("customers.field.displayName")}
          value={form.displayName}
          error={fieldErrors["displayName"] ?? null}
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label={t("common.fields.phone")}
            type="tel"
            value={form.phone}
            error={fieldErrors["phone"] ?? null}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <TextInput
            label={t("common.fields.email")}
            type="email"
            value={form.email}
            error={fieldErrors["email"] ?? null}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <TextInput
          label={t("common.fields.notes")}
          value={form.notes}
          error={fieldErrors["notes"] ?? null}
          hint={t("customers.field.notesHint")}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </div>
    </Modal>
  );
}
